<template>
  <svg
    :viewBox="`0 0 ${width} ${height}`"
    :width="width"
    :height="height"
    role="img"
    :aria-label="ariaLabel"
    class="block"
  >
    <polyline
      v-if="path"
      :points="path"
      fill="none"
      :stroke="stroke"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      vector-effect="non-scaling-stroke"
    />
    <circle
      v-if="lastPoint"
      :cx="lastPoint.x"
      :cy="lastPoint.y"
      r="2.2"
      :fill="stroke"
    />
  </svg>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    series: Array<number | null>;
    width?: number;
    height?: number;
    stroke?: string;
    ariaLabel?: string;
  }>(),
  {
    width: 80,
    height: 24,
    stroke: "currentColor",
    ariaLabel: "trend sparkline",
  },
);

const computedView = computed(() => {
  const values = props.series.filter((v): v is number => v != null);
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = props.width / (props.series.length - 1);
  const margin = 2;
  const usableHeight = props.height - margin * 2;
  const points: Array<{ x: number; y: number }> = [];
  props.series.forEach((v, i) => {
    if (v == null) return;
    const x = i * stepX;
    const y = margin + (1 - (v - min) / range) * usableHeight;
    points.push({ x, y });
  });
  return points;
});

const path = computed(() => {
  if (!computedView.value) return "";
  return computedView.value.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
});
const lastPoint = computed(() => computedView.value?.[computedView.value.length - 1] ?? null);
</script>
